import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { TimePropertyDTO } from 'business/models/Models';
import { BackgroundJobDTO } from 'components/backgroundjobs/backgroundJobs';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import NewProgressBar from 'components/main/Setup/Imaging/ImageManagementTask/Results/NewProgressBar';
import { JobTypeDTO } from 'components/main/Setup/Imaging/ImageManagementTask/constants';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import { useRMDispatch } from 'redux/hooks';
import { setSetupTab } from 'redux/reducers/setupReducer';
import { dismissBackgroundJobThunk } from 'redux/thunks/backgroundJobs';
import {
  getArchivingArchivedCount,
  getArchivingArchivedLabel,
} from './hooks/useArchivingProgressHub';
import { getArchivingJobErrorMessage } from './hooks';
import {
  DISMISSABLE_ARCHIVING_NOTIFICATION_VIEWS,
  formatArchivingScheduleTime,
  getArchivingFailureSummary,
  getArchivingNotificationView,
} from './utils';
import {
  ARCHIVING_ARCHIVED_PROGRESS_LABEL,
  ARCHIVING_COMPLETED_LABEL,
  ARCHIVING_DISMISS_ARIA_LABEL,
  ARCHIVING_IN_PROGRESS_LABEL,
  ARCHIVING_PAUSED_LABEL,
  ARCHIVING_RESULTS_JOB_ID_PARAM,
  ARCHIVING_RESULTS_PATH,
  ARCHIVING_SCHEDULED_MESSAGE,
  ARCHIVING_START_OVER_TITLE,
  UNARCHIVING_COMPLETED_LABEL,
  UNARCHIVING_DISMISS_ARIA_LABEL,
  UNARCHIVING_IN_PROGRESS_LABEL,
  UNARCHIVING_RESULTS_PATH,
  UNARCHIVING_SCHEDULED_MESSAGE,
} from './constants';
import { UNARCHIVING_ERROR_FALLBACK_MESSAGE } from './unarchiving/constants';
import { PropertyConstants } from 'enums/PropertyConstants';

const PAUSED_STORAGE_BAR_COLOR = '#dc3545';
const PAUSED_BY_USER_BAR_COLOR = '#f2c64b';
const INPROGRESS_BAR_COLOR = '#2196F3';

type ArchivingNotificationCardProps = {
  job: BackgroundJobDTO | null;
  onSeeResult: () => void;
};
const ArchivingNotificationCard = ({
  job,
  onSeeResult,
}: ArchivingNotificationCardProps) => {
  const isUnarchiving = job?.jobType === JobTypeDTO.Unarchive;
  const view = useMemo(() => getArchivingNotificationView(job), [job]);
  const dispatch = useRMDispatch();
  const navigate = useNavigate();
  const scheduledStartTime = useSystemProperty<TimePropertyDTO>(
    PropertyConstants.ArchivingStartTime,
    '',
    { refreshOnUpdate: true },
  );
  const formattedStartTime = useMemo(
    () => formatArchivingScheduleTime(scheduledStartTime as string),
    [scheduledStartTime],
  );

  if (!view || !job) {
    return null;
  }

  const handleDismiss = () => {
    dispatch(dismissBackgroundJobThunk({ id: job.jobId, type: job.jobType }));
  };

  const handleSeeResult = () => {
    dispatch(setSetupTab('archiving'));
    const resultsPath = isUnarchiving ? UNARCHIVING_RESULTS_PATH : ARCHIVING_RESULTS_PATH;
    navigate(`${resultsPath}&${ARCHIVING_RESULTS_JOB_ID_PARAM}=${job.jobId}`);
    onSeeResult();
  };

  const archivedLabel = getArchivingArchivedLabel(job);
  const inProgressLabel = isUnarchiving
    ? UNARCHIVING_IN_PROGRESS_LABEL
    : ARCHIVING_IN_PROGRESS_LABEL;
  const completedLabel = isUnarchiving
    ? UNARCHIVING_COMPLETED_LABEL
    : ARCHIVING_COMPLETED_LABEL;
  const failureFallbackMessage = isUnarchiving
    ? UNARCHIVING_ERROR_FALLBACK_MESSAGE
    : undefined;

  const renderBody = () => {
    switch (view) {
      case 'scheduled':
        return (
          <span className="archiving-notification-text">
            {isUnarchiving ? UNARCHIVING_SCHEDULED_MESSAGE : ARCHIVING_SCHEDULED_MESSAGE}{' '}
            {formattedStartTime}
          </span>
        );
      case 'inProgress':
        return (
          <>
            <span className="archiving-notification-text">
              {inProgressLabel}
              {archivedLabel ? ` (${archivedLabel})` : ''}
            </span>
            <NewProgressBar
              mainContainerClassName="flex flex-column w-full mt-1"
              progressBarContainerClass="w-[100%] taskProgress h-[0.5rem]"
              currentProgress={job.progress}
              progressWidth={job.progress}
              backgroundColor={INPROGRESS_BAR_COLOR}
              showProgressDigits={false}
              showTitle={false}
            />
          </>
        );
      // Archiving-only: UnarchiveJob never reports WaitingForStorage.
      case 'pausedStorage':
        return (
          <>
            <span className="archiving-notification-text">
              {ARCHIVING_IN_PROGRESS_LABEL}
              {archivedLabel ? ` (${archivedLabel})` : ''}
            </span>
            <NewProgressBar
              mainContainerClassName="flex flex-column w-full mt-1"
              progressBarContainerClass="w-[100%] taskProgress h-[0.5rem]"
              currentProgress={job.progress}
              progressWidth={job.progress}
              backgroundColor={PAUSED_STORAGE_BAR_COLOR}
              showProgressDigits={false}
              showTitle={false}
            />
            <span className="archiving-notification-text archiving-notification-error">
              {getArchivingJobErrorMessage(job)}
            </span>
          </>
        );
      // Archiving-only: UnarchiveJob never reports Paused (by user).
      case 'pausedByUser':
        return (
          <>
            <span className="archiving-notification-text">{ARCHIVING_PAUSED_LABEL}</span>
            {archivedLabel && (
              <span className="archiving-notification-subtext">
                {ARCHIVING_ARCHIVED_PROGRESS_LABEL} ({archivedLabel})
              </span>
            )}
            <NewProgressBar
              mainContainerClassName="flex flex-column w-full mt-1"
              progressBarContainerClass="w-[100%] taskProgress h-[0.5rem]"
              currentProgress={job.progress}
              progressWidth={job.progress}
              backgroundColor={PAUSED_BY_USER_BAR_COLOR}
              showProgressDigits={false}
              showTitle={false}
            />
          </>
        );
      // Archiving-only: UnarchiveJob has no Start Over feature.
      case 'startingOver':
        return (
          <>
            <span className="archiving-notification-text">
              {ARCHIVING_START_OVER_TITLE}
            </span>
            <span className="archiving-notification-text">
              Restore experiment data{archivedLabel ? ` (${archivedLabel})` : ''}
            </span>
            <NewProgressBar
              mainContainerClassName="flex flex-column w-full mt-1"
              progressBarContainerClass="w-[100%] taskProgress h-[0.5rem]"
              currentProgress={job.progress}
              progressWidth={job.progress}
              backgroundColor={INPROGRESS_BAR_COLOR}
              showProgressDigits={false}
              showTitle={false}
            />
          </>
        );
      // Archiving-only: UnarchiveJob has no Start Over feature.
      case 'startedOver': {
        const restored = getArchivingArchivedCount(job);
        return (
          <>
            <span className="archiving-notification-text">
              {ARCHIVING_START_OVER_TITLE}
            </span>
            <span className="archiving-notification-text">
              Experiment data restored{restored ? ` (${restored})` : ''}
            </span>
          </>
        );
      }
      case 'completed': {
        const total = getArchivingArchivedCount(job);
        const failureSummary = getArchivingFailureSummary(job);
        return (
          <>
            <span className="archiving-notification-text">
              {completedLabel}
              {total ? ` (${total})` : ''}
            </span>
            {failureSummary && (
              <span className="archiving-notification-text archiving-notification-error">
                {failureSummary}
              </span>
            )}
            <button
              className="archiving-notification-see-result"
              onClick={handleSeeResult}
            >
              SEE RESULT
            </button>
          </>
        );
      }
      case 'failed':
        return (
          <span className="archiving-notification-text archiving-notification-error">
            {getArchivingJobErrorMessage(job, failureFallbackMessage)}
          </span>
        );
      default:
        return null;
    }
  };

  const showDismiss = DISMISSABLE_ARCHIVING_NOTIFICATION_VIEWS.includes(view);

  return (
    <div
      className="archiving-notification-card"
      data-testid={
        isUnarchiving ? 'unarchiving-notification-card' : 'archiving-notification-card'
      }
    >
      <div className="archiving-notification-header">
        <RMSvgIcon name="Archiving" width={25} height={25} />
        <div className="archiving-notification-content">{renderBody()}</div>
        {showDismiss && (
          <button
            className="archiving-notification-dismiss"
            onClick={handleDismiss}
            aria-label={
              isUnarchiving
                ? UNARCHIVING_DISMISS_ARIA_LABEL
                : ARCHIVING_DISMISS_ARIA_LABEL
            }
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

export default ArchivingNotificationCard;
